"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/lib/format/date";
import { Clock, Eye, CheckCircle2, XCircle, Archive } from "lucide-react";

interface MeasurementVersionItem {
  id: string;
  versionNo: number;
  status: string;
  capturedAt: string;
  capturedBy?: {
    name: string;
    publicId: string;
  };
  bustCm: number;
  waistCm: number;
  hipCm: number;
  shoulderCm: number;
  sleeveLengthCm: number;
  dressLengthCm: number;
}

interface MeasurementVersionListProps {
  versions: MeasurementVersionItem[];
  onViewVersion?: (versionId: string) => void;
  className?: string;
  title?: string;
}

const statusConfig: Record<string, { icon: typeof Clock; color: string; label: string }> = {
  DRAFT: { icon: Clock, color: "text-gray-500", label: "Draft" },
  SUBMITTED: { icon: Clock, color: "text-blue-500", label: "Submitted" },
  PENDING_ACCEPTANCE: { icon: Clock, color: "text-amber-500", label: "Pending Acceptance" },
  ACCEPTED_ACTIVE: { icon: CheckCircle2, color: "text-emerald-500", label: "Active" },
  REJECTED: { icon: XCircle, color: "text-red-500", label: "Rejected" },
  SUPERSEDED: { icon: Archive, color: "text-slate-400", label: "Superseded" },
};

export function MeasurementVersionList({
  versions,
  onViewVersion,
  className,
  title = "Version History",
}: MeasurementVersionListProps) {
  if (versions.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="py-8 text-center text-muted-foreground">
          No measurement versions found.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          {versions.map((version) => {
            const config = statusConfig[version.status] || statusConfig.DRAFT;
            const StatusIcon = config.icon;

            return (
              <div
                key={version.id}
                className={cn(
                  "flex items-center justify-between p-3 rounded-lg border",
                  version.status === "ACCEPTED_ACTIVE" && "border-emerald-200 bg-emerald-50/50",
                  version.status === "PENDING_ACCEPTANCE" && "border-amber-200 bg-amber-50/50"
                )}
              >
                <div className="flex items-center gap-3">
                  <div className={cn("p-1.5 rounded-full bg-muted", config.color)}>
                    <StatusIcon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">Version {version.versionNo}</span>
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-xs",
                          version.status === "ACCEPTED_ACTIVE" && "border-emerald-300 text-emerald-700",
                          version.status === "PENDING_ACCEPTANCE" && "border-amber-300 text-amber-700"
                        )}
                      >
                        {config.label}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatDateTime(version.capturedAt)}
                      {version.capturedBy && ` by ${version.capturedBy.name}`}
                    </p>
                  </div>
                </div>
                {onViewVersion && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewVersion(version.id)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
